import {
  SKILL_MY_SHARE_PATH_SEGMENT,
  SKILL_SHARE_CANDIDATES_PATH_SEGMENT,
  SKILL_SHARES_PATH_SEGMENT,
  SKILLS_BASE_PATH,
  SKILLS_SHARED_WITH_ME_PATH,
} from "../constants/skills";
import type {
  TCreateSkillSharesRequest,
  TLeaveSkillShareResponse,
  TListSkillShareCandidatesQuery,
  TListSkillSharesQuery,
  TListSkillSharesSearchQuery,
  TRevokeSkillShareResponse,
  TSkillShareCandidatesResponse,
  TSkillShareMutationResponse,
  TSkillSharesListResponse,
  TSkillSharesMutationResponse,
  TSkillsListResponse,
  TUpdateOwnSkillShareRequest,
  TUpdateOwnSkillShareResponse,
  TUpdateSkillShareRequest,
} from "../types/skills";
import { encodePathSegment } from "../utils/encode-path-segment";
import type { ApiClient } from "./client";
import {
  appendSkillPagingParams,
  appendSkillSearchParam,
} from "./skill-query-params";

export interface IListSkillSharesParams {
  skillUuid: string;
  query?: TListSkillSharesQuery;
}

export interface IListSkillShareCandidatesParams {
  skillUuid: string;
  query?: TListSkillShareCandidatesQuery;
}

export interface ICreateSkillSharesParams {
  skillUuid: string;
  payload: TCreateSkillSharesRequest;
}

export interface IUpdateSkillShareParams {
  skillUuid: string;
  shareUuid: string;
  payload: TUpdateSkillShareRequest;
}

export interface IRevokeSkillShareParams {
  skillUuid: string;
  shareUuid: string;
}

// NOTE: NSAI-6578 — the recipient's own share is named by the skill alone.
export interface ILeaveSkillShareParams {
  skillUuid: string;
}

export interface IUpdateOwnSkillShareParams {
  skillUuid: string;
  payload: TUpdateOwnSkillShareRequest;
}

// NOTE: NSAI-3309 — the private skill sharing client shared by web and mobile.
//
// It makes NO authorization decision. Only a skill's owner can reach the four
// management routes; a caller who cannot see the skill gets the same 404 a
// non-existent uuid gets, and this class neither predicts nor softens that.
//
// A recipient's address reaches a client only through the owner-only management
// and candidate routes. `getSharedWithMe` returns ordinary skill rows — no
// recipient list and no owner address — because a recipient cannot manage shares.
export class SkillSharesApi {
  constructor(private readonly apiClient: ApiClient) {}

  private buildSharesPath(skillUuid: string): string {
    return `${SKILLS_BASE_PATH}/${encodePathSegment(skillUuid)}/${SKILL_SHARES_PATH_SEGMENT}`;
  }

  private buildMySharePath(skillUuid: string): string {
    return `${SKILLS_BASE_PATH}/${encodePathSegment(skillUuid)}/${SKILL_MY_SHARE_PATH_SEGMENT}`;
  }

  async listShares({
    skillUuid,
    query = {},
  }: IListSkillSharesParams): Promise<TSkillSharesListResponse> {
    const params = appendSkillPagingParams({
      params: new URLSearchParams(),
      page: query.page,
      limit: query.limit,
    });
    const queryString = params.toString();
    const response = await this.apiClient.get<TSkillSharesListResponse>(
      queryString
        ? `${this.buildSharesPath(skillUuid)}?${queryString}`
        : this.buildSharesPath(skillUuid),
    );
    return response.data;
  }

  async listShareCandidates({
    skillUuid,
    query = {},
  }: IListSkillShareCandidatesParams): Promise<TSkillShareCandidatesResponse> {
    const params = appendSkillPagingParams({
      params: new URLSearchParams(),
      page: query.page,
      limit: query.limit,
    });

    appendSkillSearchParam({ params, search: query.search });

    const basePath = `${SKILLS_BASE_PATH}/${encodePathSegment(skillUuid)}/${SKILL_SHARE_CANDIDATES_PATH_SEGMENT}`;
    const queryString = params.toString();
    const response = await this.apiClient.get<TSkillShareCandidatesResponse>(
      queryString ? `${basePath}?${queryString}` : basePath,
    );
    return response.data;
  }

  async createShares({
    skillUuid,
    payload,
  }: ICreateSkillSharesParams): Promise<TSkillSharesMutationResponse> {
    const response = await this.apiClient.post<TSkillSharesMutationResponse>(
      this.buildSharesPath(skillUuid),
      payload,
    );
    return response.data;
  }

  async updateShare({
    skillUuid,
    shareUuid,
    payload,
  }: IUpdateSkillShareParams): Promise<TSkillShareMutationResponse> {
    const response = await this.apiClient.patch<TSkillShareMutationResponse>(
      `${this.buildSharesPath(skillUuid)}/${encodePathSegment(shareUuid)}`,
      payload,
    );
    return response.data;
  }

  async revokeShare({
    skillUuid,
    shareUuid,
  }: IRevokeSkillShareParams): Promise<TRevokeSkillShareResponse> {
    const response = await this.apiClient.delete<TRevokeSkillShareResponse>(
      `${this.buildSharesPath(skillUuid)}/${encodePathSegment(shareUuid)}`,
    );
    return response.data;
  }

  // NOTE: NSAI-6578 — the recipient removing themselves from a share. The server
  // answers 404 for anything but the caller's own live share, owner included.
  async leaveShare({
    skillUuid,
  }: ILeaveSkillShareParams): Promise<TLeaveSkillShareResponse> {
    const response = await this.apiClient.delete<TLeaveSkillShareResponse>(
      this.buildMySharePath(skillUuid),
    );
    return response.data;
  }

  // NOTE: NSAI-6578 — the recipient's own discovery preference. It changes what
  // the model offers THEM on its own, never access or anyone else's discovery.
  async updateOwnShare({
    skillUuid,
    payload,
  }: IUpdateOwnSkillShareParams): Promise<TUpdateOwnSkillShareResponse> {
    const response = await this.apiClient.patch<TUpdateOwnSkillShareResponse>(
      this.buildMySharePath(skillUuid),
      payload,
    );
    return response.data;
  }

  async getSharedWithMe(
    query: TListSkillSharesSearchQuery = {},
  ): Promise<TSkillsListResponse> {
    const params = appendSkillPagingParams({
      params: new URLSearchParams(),
      page: query.page,
      limit: query.limit,
    });
    appendSkillSearchParam({ params, search: query.search });

    const queryString = params.toString();
    const response = await this.apiClient.get<TSkillsListResponse>(
      queryString
        ? `${SKILLS_SHARED_WITH_ME_PATH}?${queryString}`
        : SKILLS_SHARED_WITH_ME_PATH,
    );
    return response.data;
  }
}
